import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { RangeModel } from '../sol-range-slider-wrapper/models/range-slider-model';

@Component({
    selector: 'cv-cxone-range-slider',
    templateUrl: './cxone-range-slider.component.html',
    styleUrl: './cxone-range-slider.component.scss'
})
export class CxoneRangeSliderComponent implements OnChanges {
    @Input() min: number; // Default to 0 seconds
    @Input() max: number; // Default to 10 hours
    @Input() metricConversionType: number; // 0 for percentage, 1 for seconds, 2 for minutes, 3 for hours, 4 for days, 5 for number
    @Input() metricDirection: number; // 1 for increasing, 0 for decreasing
    @Input() colorClasses: string[] = [];
    @Input() startPoints: (string | number)[] = [];
    @Output() rangesChanged = new EventEmitter<RangeModel[]>();
    @Output() isRangesOverlap: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() isQualityEvaluationWidget = false;

    public markerValues = 11;
    public step = 0.1;
    public connect: boolean[] = [true, true, false, true, true];
    public showSlider = true;
    public isTimeType = false;
    public range: any;
    public rangePips: any;

    constructor(private cdr: ChangeDetectorRef) { }

    ngOnChanges(changes: SimpleChanges): void {
        this.isTimeType = [1, 2, 3, 4].includes(this.metricConversionType);
        if (changes.min || changes.max || changes.startPoints) {
            let minValue = this.min;
            let maxValue = this.max;
            if (changes.min?.firstChange === false) {
                minValue = changes.min.currentValue;
                this.resetStartPointsAndColorClasses();
            }
            if (changes.max?.firstChange === false) {
                maxValue = changes.max.currentValue;
                this.resetStartPointsAndColorClasses();
            }
            if (changes?.startPoints) {
                this.startPoints = changes.startPoints.currentValue;
            }
            if (changes.isQualityEvaluationWidget?.currentValue) {
                this.min = 0;
                this.max = 10;
                minValue = this.min;
                maxValue = this.max;
            }
            this.initializeSlider(minValue, maxValue);
            this.setMarkerValue();
        }
    }

    private initializeSlider(min: number, max: number): void {
        const qualityEvaluationDefaultStartPointsInPercentages = [0.02, 0.45, 0.53, 0.91];
        const otherDefaultStartPointsInPercentages = [0.105, 0.305, 0.705, 0.905];
        const defaultStartPointsInPercentages = this.isQualityEvaluationWidget ? qualityEvaluationDefaultStartPointsInPercentages : otherDefaultStartPointsInPercentages;
        this.rangePips = this.returnPipesOptions();
        this.range = this.returnRange();
 
        if (!this.startPoints || this.startPoints.length === 0) {
            this.startPoints = this.generateStartPoints(min, max, defaultStartPointsInPercentages);
        }
        this.patchSliderValues(this.startPoints);
    }

    private setMarkerValue(): void {
        if (this.max - this.min <= 10) {
            const truncatedValues = Math.trunc((this.max - this.min) + 1);
            this.markerValues = truncatedValues < 2 ? 2 : truncatedValues;
        } else {
            this.markerValues = 11;
        }
    }

    private patchSliderValues(startPoints: (string | number)[]): void {
        switch (startPoints.length) {
            case 1:
                this.connect = [true, true];
                this.setColorClasses(["strongly-negative-connect", "strongly-positive-connect"]);
                break;
            case 2:
                this.connect = [true, false, true];
                this.setColorClasses(["strongly-negative-connect", "strongly-positive-connect"]);
                break;
            case 3:
                this.connect = [true, true, true, true];
                this.setColorClasses(["strongly-negative-connect", "negative-connect", "positive-connect", "strongly-positive-connect"]);
                break;
            case 4:
                this.connect = [true, true, false, true, true];
                this.setColorClasses(["strongly-negative-connect", "negative-connect", "positive-connect", "strongly-positive-connect"]);
                break;
        }
        this.forceSliderRerender();
    }
 
    public resetStartPointsAndColorClasses(): void {
        this.startPoints = [];
        this.colorClasses = [];
    }
 
    private returnRange(): any {
        let partOfRange = (this.max - this.min) / 100; // Calculate 1% of the range
        return {
            min: [this.min, partOfRange],
            max: [this.max]
        };
    }

    private returnPipesOptions(): any {
        let nodePoints = [];
        const stepOnSlider = 10; // Aim for 10 steps on the slider
        const diffMaxMin = this.max - this.min;
        const step = Math.max(diffMaxMin / stepOnSlider, 0.1); // Ensure minimum step of 0.1
        const increment = diffMaxMin <= stepOnSlider ? 1 : diffMaxMin / stepOnSlider;
        if (this.max > this.min && step > 0) {
            for (let i = this.min; i <= this.max; i += increment) {
                nodePoints.push(parseFloat(i.toFixed(1)));
            }
        }
        return {
            mode: 'steps',
            filter: function nonLinearFilterPips(value: any, type: any) {
                if (type === 0) {
                    return 1;
                }
                return value === 0 || nodePoints.includes(value)
                    ? 1
                    : 0;
            },
            density: 60,
            format: {
                to: function (seconds: any) {
                    if (seconds >= 3600) {
                        const hours = seconds / 3600;
                        return parseFloat(hours.toFixed(2)) + 'h';
                    } else if (seconds >= 60) {
                        const minutes = seconds / 60;
                        return parseFloat(minutes.toFixed(2)) + 'm';
                    } else {
                        return Math.floor(seconds) + 's';
                    }
                },
                from: function (formattedTime: any): number {
                    const parts = formattedTime.split(' ');
                    if (parts.length === 3) {
                        const value1 = parseInt(parts[0], stepOnSlider);
                        const value2 = parseInt(parts[1], stepOnSlider);
                        if (parts[1] === 'h') {
                            return value1 * 3600 + value2 * 60;
                        } else if (parts[1] === 'm') {
                            return value1 * 60 + value2;
                        }
                        return 0;
                    } else {
                        return parseInt(parts[0], stepOnSlider);
                    }
                }
            }
        };
    }

    public timeTooltipFormatter(seconds: number): string {
        if (seconds >= 3600) {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            return hours.toString().padStart(2) + 'h ' + minutes.toString().padStart(2) + 'm ';
        } else if (seconds >= 60) {
            const minutes = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return (
                (minutes ? minutes.toString().padStart(2) + 'm ' : '') +
                (secs ? secs.toString().padStart(2) + 's' : '')
            );
        } else {
            return Math.round(seconds) + 's';
        }
    };

    public tooltipFormatter(tooltipValue: number): string {
        const formattedValue = Number(tooltipValue).toFixed(2);
        return formattedValue;
    }

    public onRangeChange(updatedRange): void {
        const ranges = Array.isArray(updatedRange) && updatedRange.length > 0
            ? this.generateRanges(updatedRange)
            : this.generateRanges([updatedRange]);

        if (ranges.length === 0 || ranges.length === this.startPoints.length) {
            this.isRangesOverlap.emit(true);
        } else {
            this.rangesChanged.emit(ranges);
            this.isRangesOverlap.emit(false);
        }
    }

    private parseTimeToSeconds(timeStr: string): number {
        const timeFormatRegex = /(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?\s*(?:(\d+)\s*s)?/; // Matches "1h 30m 45s", "30m", "45s", etc.
        const match = timeStr.trim().match(timeFormatRegex);

        if (!match) return 0;

        const hours = parseInt(match[1] || '0', 10);
        const minutes = parseInt(match[2] || '0', 10);
        const seconds = parseInt(match[3] || '0', 10);

        return hours * 3600 + minutes * 60 + seconds;
    }

    private generateRanges(values: (string | number)[]): RangeModel[] {
        const numericOrPercentRegx = /^\d+%?$/; // Matches numeric values or percentages
        const normalizedValues = values.map(val => {
            if (typeof val === 'number') return val;
            if (numericOrPercentRegx.test(val.trim())) return parseInt(val.trim(), 10);
            return this.parseTimeToSeconds(val);
        });

        normalizedValues.push(this.max, this.min);
        normalizedValues.sort((a, b) => b - a);

        const hasDuplicates = new Set(normalizedValues).size !== normalizedValues.length; // Check for duplicate values in range
        if (hasDuplicates) {
            return [];
        }
        return this.buildRangesFromValues(normalizedValues);
    }

    private buildRangesFromValues(normalizedValues: number[]): RangeModel[] {
        const ranges: RangeModel[] = [];
        for (let i = 0; i < normalizedValues.length - 1; i++) {
            if (i === normalizedValues.length - 2) {
                ranges.push({
                    enabled: true,
                    left: normalizedValues[i + 1],
                    right: normalizedValues[i],
                });
            } else {
                ranges.push({
                    enabled: true,
                    left: normalizedValues[i + 1] + 0.01,
                    right: normalizedValues[i],
                });
            }
        }
        return ranges;
    }

    private setColorClasses(classes: string[]) {
        this.colorClasses = this.metricDirection === 0 ? [...classes].reverse() : [...classes];
    }

    private generateStartPoints(min: number, max: number, percentages: number[]): number[] {
        const generatedStartPoints = percentages.map(p => min + (max - min) * p);
        let generatedStartPointsWithMinMax = [min, ...generatedStartPoints, max].reverse();
        const generatedRangesForDefaultStartPoints = this.buildRangesFromValues(generatedStartPointsWithMinMax);
        this.rangesChanged.emit(generatedRangesForDefaultStartPoints);
        return generatedStartPoints;
    }

    public addRangeToSlider() {
        // Validation: Maximum of 4 ranges (startPoints)
        if (this.startPoints.length >= 4) {
            return;
        }

        switch (this.startPoints.length) {
            case 1:
                this.startPoints = this.generateStartPoints(this.min, this.max, [0.33, 0.67]);
                this.patchSliderValues(this.startPoints);
                break;
            case 2:
                this.startPoints = this.generateStartPoints(this.min, this.max, [0.25, 0.5, 0.75]);
                this.patchSliderValues(this.startPoints);
                break;
            case 3:
                this.startPoints = this.generateStartPoints(this.min, this.max, [0.105, 0.305, 0.705, 0.905]);
                this.patchSliderValues(this.startPoints);
                break;
        }
        this.isRangesOverlap.emit(false);
    }

    public removeRangeFromSlider() {
        // Validation: Minimum of 1 range (startPoints)
        if (this.startPoints.length <= 1) {
            return;
        }

        switch (this.startPoints.length) {
            case 4:
                this.startPoints = this.generateStartPoints(this.min, this.max, [0.25, 0.5, 0.75]);
                this.patchSliderValues(this.startPoints);
                break;
            case 3:
                this.startPoints = this.generateStartPoints(this.min, this.max, [0.33, 0.67]);
                this.patchSliderValues(this.startPoints);
                break;
            case 2:
                this.startPoints = this.generateStartPoints(this.min, this.max, [0.5]);
                this.patchSliderValues(this.startPoints);
                break;
        }
        this.isRangesOverlap.emit(false);
    }

    public forceSliderRerender() {
        this.showSlider = false;
        setTimeout(() => {
            this.showSlider = true;
            this.cdr.detectChanges();
        }, 10);
    }
}
